import { Link } from "react-router-dom";
import { Facebook, Instagram, Linkedin, Youtube, Phone, MapPin, ArrowRight } from "lucide-react";

const XIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const TikTokIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-navy text-white">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 grid gap-12 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <div className="mb-5">
            <span className="bg-white rounded-sm px-3 py-2 inline-flex">
              <img src="/images/logo-cropped.png" alt="HLM Moving" className="h-9 w-auto" />
            </span>
          </div>
          <p className="text-sm text-white/60 leading-relaxed">
            Commercial moving services that meet your needs — office and facility relocation,
            workplace solutions, and employee mobility from our Ashburn, Virginia headquarters.
          </p>
          <div className="flex gap-3 mt-6">
            <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" aria-label="Facebook"
              data-testid="footer-social-facebook"
              className="w-9 h-9 border border-white/20 flex items-center justify-center rounded-sm hover:bg-brand hover:border-brand transition-colors">
              <Facebook size={16} />
            </a>
            <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" aria-label="Instagram"
              data-testid="footer-social-instagram"
              className="w-9 h-9 border border-white/20 flex items-center justify-center rounded-sm hover:bg-brand hover:border-brand transition-colors">
              <Instagram size={16} />
            </a>
            <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"
              data-testid="footer-social-linkedin"
              className="w-9 h-9 border border-white/20 flex items-center justify-center rounded-sm hover:bg-brand hover:border-brand transition-colors">
              <Linkedin size={16} />
            </a>
            <a href="https://tiktok.com" target="_blank" rel="noopener noreferrer" aria-label="TikTok"
              data-testid="footer-social-tiktok"
              className="w-9 h-9 border border-white/20 flex items-center justify-center rounded-sm hover:bg-brand hover:border-brand transition-colors">
              <TikTokIcon size={15} />
            </a>
            <a href="https://x.com" target="_blank" rel="noopener noreferrer" aria-label="X"
              data-testid="footer-social-x"
              className="w-9 h-9 border border-white/20 flex items-center justify-center rounded-sm hover:bg-brand hover:border-brand transition-colors">
              <XIcon size={15} />
            </a>
            <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" aria-label="YouTube"
              data-testid="footer-social-youtube"
              className="w-9 h-9 border border-white/20 flex items-center justify-center rounded-sm hover:bg-brand hover:border-brand transition-colors">
              <Youtube size={16} />
            </a>
          </div>
        </div>

        <div>
          <h4 className="font-display font-semibold text-sm uppercase tracking-[0.2em] text-brand mb-5">Company</h4>
          <ul className="space-y-3 text-sm text-white/70">
            <li><Link to="/" data-testid="footer-link-home" className="hover:text-brand transition-colors">Home</Link></li>
            <li><Link to="/leadership" data-testid="footer-link-leadership" className="hover:text-brand transition-colors">Leadership</Link></li>
            <li><Link to="/industries" data-testid="footer-link-industries" className="hover:text-brand transition-colors">Industries We Serve</Link></li>
            <li><Link to="/resources" data-testid="footer-link-resources" className="hover:text-brand transition-colors">Resources</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-display font-semibold text-sm uppercase tracking-[0.2em] text-brand mb-5">Services</h4>
          <ul className="space-y-3 text-sm text-white/70">
            <li><Link to="/workplace-solutions" data-testid="footer-link-workplace" className="hover:text-brand transition-colors">Workplace Solutions</Link></li>
            <li><Link to="/workplace-solutions" data-testid="footer-link-decommissioning" className="hover:text-brand transition-colors">Decommissioning</Link></li>
            <li><Link to="/workplace-solutions" data-testid="footer-link-lab" className="hover:text-brand transition-colors">Biotech &amp; Lab Services</Link></li>
            <li><Link to="/residential-moving" data-testid="footer-link-residential" className="hover:text-brand transition-colors">Residential Moving</Link></li>
            <li><Link to="/workplace-solutions" data-testid="footer-link-flooring" className="hover:text-brand transition-colors">Commercial Flooring</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-display font-semibold text-sm uppercase tracking-[0.2em] text-brand mb-5">Headquarters</h4>
          <p className="text-sm text-white/70 flex items-start gap-2 mb-3">
            <MapPin size={15} className="text-brand mt-0.5 shrink-0" />
            23710 Schooler Plaza #87,<br />Ashburn VA 20148
          </p>
          <a href="tel:+15718509694" data-testid="footer-phone-link"
            className="text-sm text-white/70 flex items-center gap-2 hover:text-brand transition-colors mb-6">
            <Phone size={15} className="text-brand" /> (571) 850-9694
          </a>
          <Link
            to="/contact"
            data-testid="footer-contact-us-btn"
            className="inline-flex items-center gap-2 bg-brand hover:bg-brand-hover text-white text-sm font-bold uppercase tracking-wide px-6 py-3 rounded-sm transition-colors"
          >
            Contact Us <ArrowRight size={15} />
          </Link>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-xs text-white/40" data-testid="footer-copyright">© 2026 HLM Moving. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <p className="text-xs text-white/40">Precision Commercial &amp; Residential Relocation</p>
            <Link to="/admin" data-testid="footer-admin-link" className="text-xs text-white/40 hover:text-brand transition-colors">Admin</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
